import React, { useState } from 'react';
import axios from 'axios';

import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import CustomerDetailsForm from './components/CustomerDetailsForm';
import ServiceSelectionForm from './components/ServiceSelectionForm';
import DateTimeSelector from './components/DateTimeSelector';
import LocationSelector from './components/LocationSelector';
import SpecialInstructions from './components/SpecialInstructions';
import BookingSummary from './components/BookingSummary';
import BookingConfirmationModal from './components/BookingConfirmationModal';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { createRazorpayOptions } from '../../config/razorpay';

const PickupScheduling = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmedBookingId, setConfirmedBookingId] = useState('');

  // Form data states
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    address: '',
    pincode: '',
    area: ''
  });

  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [instructions, setInstructions] = useState('');
  const [preferences, setPreferences] = useState([]);

  // Error states
  const [errors, setErrors] = useState({});

  // --- OLD SERVICES (Commented) ---
  /*
  const servicesOld = [
    { id: 'wash-fold', name: 'Wash & Fold', price: 149 },
    { id: 'express-wash', name: 'Express Wash', price: 199 },
    { id: 'dry-cleaning', name: 'Dry Cleaning', price: 299 },
    { id: 'premium-pressing', name: 'Premium Pressing', price: 99 },
    { id: 'delicate-wash', name: 'Delicate Wash', price: 249 },
    { id: 'eco-wash', name: 'Eco-Friendly Wash', price: 179 },
    { id: 'curtain-cleaning', name: 'Curtain & Drape Cleaning', price: 399 },
    { id: 'pickup-delivery', name: 'Pickup & Delivery', price: 0 }
  ];
  */
  // --- END OLD SERVICES ---

  // New 9 Services (from services-overview)
  const services = [
    {
      id: 'dry-cleaning',
      name: 'Dry Cleaning',
      price: 90,
      unit: '/ Item',
      description: 'Professional dry cleaning for suits, dresses, and delicate fabrics requiring special care.',
      icon: 'Shirt',
      category: 'dry-clean'
    },
    {
      id: 'laundry-wash-fold',
      name: 'Laundry Wash & Fold',
      price: 119,
      unit: '/ Kg',
      description: 'Complete washing, drying, and folding service for your everyday garments with premium detergents.',
      icon: 'WashingMachine',
      category: 'wash'
    },
    {
      id: 'laundry-wash-iron',
      name: 'Laundry Wash & Iron',
      price: 149,
      unit: '/ Kg',
      description: 'Wash, dry, and expert pressing service for crisp, wrinkle-free garments ready to wear.',
      icon: 'Iron',
      category: 'wash'
    },
    {
      id: 'steam-press',
      name: 'Steam Press',
      price: 20,
      unit: '/ Item',
      description: 'Expert steam pressing and finishing for fresh, crisp garments without full wash.',
      icon: 'Iron',
      category: 'premium'
    },
    {
      id: 'toys-cleaning',
      name: 'Toys Cleaning',
      price: 120,
      unit: '/ Item',
      description: 'Gentle cleaning for soft toys, plush items, and children\'s playthings with safe detergents.',
      icon: 'Smile',
      category: 'specialty'
    },
    {
      id: 'shoe-care',
      name: 'Shoe Care',
      price: 290,
      unit: '/ Item',
      description: 'Complete shoe cleaning, polishing, and restoration for all types of footwear.',
      icon: 'Footprints',
      category: 'premium'
    },
    {
      id: 'leather-care',
      name: 'Leather Care',
      price: 150,
      unit: '/ Item',
      description: 'Specialized cleaning and conditioning for leather jackets, bags, and accessories.',
      icon: 'Briefcase',
      category: 'premium'
    },
    {
      id: 'carpet-cleaning',
      name: 'Carpet Cleaning',
      price: 40,
      unit: '/ Sq ft',
      description: 'Professional deep cleaning for carpets and rugs with stain and allergen removal.',
      icon: 'LayoutGrid',
      category: 'specialty'
    },
    {
      id: 'curtain-cleaning',
      name: 'Curtain Cleaning',
      price: 150,
      unit: '/ Kg',
      description: 'Professional cleaning for curtains, drapes, and large fabric window treatments.',
      icon: 'Home',
      category: 'dry-clean'
    },
  ];

  // Calculate total amount
  const calculateTotal = () => {
    const serviceTotal = selectedServices?.reduce((total, serviceId) => {
      const service = services?.find(s => s?.id === serviceId);
      return total + (service?.price || 0);
    }, 0);
    const expressCharge = preferences?.includes('express-service') ? 50 : 0;
    return serviceTotal + expressCharge;
  };

  const steps = [
    { id: 1, title: 'Customer Details', icon: 'User' },
    // { id: 2, title: 'Select Services', icon: 'Package' }, // Step hidden - re-enable to restore
    { id: 3, title: 'Schedule Pickup', icon: 'Calendar' },
    // { id: 4, title: 'Location & Preferences', icon: 'MapPin' }, // Step hidden - re-enable to restore
  ];

  const handleFormDataChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData?.name?.trim()) newErrors.name = 'Name is required';
        if (!formData?.mobile?.trim()) newErrors.mobile = 'Mobile number is required';
        else if (!/^\d{10}$/?.test(formData?.mobile?.replace(/\s/g, ''))) {
          newErrors.mobile = 'Please enter a valid 10-digit mobile number';
        }
        if (formData?.pincode?.trim() && !/^\d{6}$/?.test(formData?.pincode)) {
          newErrors.pincode = 'Please enter a valid 6-digit pincode';
        }
        if (formData?.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
          newErrors.email = 'Please enter a valid email address';
        }
        break;

      // case 2: // Select Services - hidden, re-enable to restore validation
      //   if (selectedServices?.length === 0) {
      //     newErrors.services = 'Please select at least one service';
      //   }
      //   break;

      case 3:
        if (!selectedDate) newErrors.date = 'Please select a pickup date';
        if (!selectedTime) newErrors.time = 'Please select a pickup time';
        break;

      // case 4: // Location & Preferences - hidden, re-enable to restore validation
      //   if (!selectedLocation) newErrors.location = 'Please select a pickup location';
      //   break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Step IDs that are active (step 2 is hidden/skipped)
  const activeStepIds = steps.map(s => s.id);

  const handleNext = () => {
    if (validateStep(currentStep)) {
      const currentIndex = activeStepIds.indexOf(currentStep);
      const nextStep = activeStepIds[currentIndex + 1];
      if (nextStep !== undefined) setCurrentStep(nextStep);
    }
  };

  const handlePrevious = () => {
    const currentIndex = activeStepIds.indexOf(currentStep);
    const prevStep = activeStepIds[currentIndex - 1];
    if (prevStep !== undefined) setCurrentStep(prevStep);
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStepIds[activeStepIds.length - 1])) return;

    // Directly process booking and send email (Razorpay bypassed)
    await handleBookingProcess();
  };

  /* 
  // Razorpay payment integration (Commented out as requested)
  const handlePayment = async () => {
    setIsProcessingPayment(true);
    
    try {
      // Load Razorpay script
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.body.appendChild(script);

      script.onload = () => {
        const options = createRazorpayOptions(
          calculateTotal(),
          null, // order_id - will be generated by backend
          {
            name: formData?.name || '',
            email: formData?.email || '',
            mobile: formData?.mobile || ''
          },
          {
            address: formData?.address || '',
            pickup_date: selectedDate || '',
            pickup_time: getTimeSlotLabel(selectedTime) || '',
            services: selectedServices?.map(serviceId => {
              const service = services?.find(s => s?.id === serviceId);
              return service?.name;
            })?.join(', ') || '',
            location: selectedLocation || ''
          }
        );

        // Add payment handler
        options.handler = function (response) {
          console.log('Payment successful:', response);
          handlePaymentSuccess(response);
        };

        // Add modal dismiss handler
        options.modal.ondismiss = function() {
          setIsProcessingPayment(false);
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
      };

      script.onerror = () => {
        console.error('Failed to load Razorpay script');
        setIsProcessingPayment(false);
        alert('Payment system is temporarily unavailable. Please try again later.');
      };

    } catch (error) {
      console.error('Payment error:', error);
      setIsProcessingPayment(false);
      alert('Payment failed. Please try again.');
    }
  };
  */

  const handleBookingProcess = async () => {
    try {
      setIsSubmitting(true);

      const emailData = {
        name: formData?.name,
        mobile: formData?.mobile,
        email: formData?.email,
        address: formData?.address,
        area: formData?.area,
        pincode: formData?.pincode,
        full_address: `${formData?.address}, ${formData?.area}, ${formData?.pincode}`,
        services: selectedServices?.map(serviceId => {
          const service = services?.find(s => s?.id === serviceId);
          return service?.name;
        })?.join(', ') || 'No services selected',
        pickup_date: selectedDate,
        pickup_time: getTimeSlotLabel(selectedTime),
        amount: calculateTotal(),
        payment_id: 'Direct Booking (Payment Offline)'
      };

      // 1. Post to the MySQL database (creates user & booking records)
      const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/bookings.php?action=create_booking`, emailData, {
        withCredentials: true // Extremely important if you want sessions to persist
      });

      if (response.data?.status === 'success') {
        if (response.data?.data?.booking_number) {
          setConfirmedBookingId(response.data.data.booking_number);
        }
        console.log('Booking successfully saved to database and email triggered.');

        setIsSubmitting(false);
        setIsProcessingPayment(false);
        setShowConfirmation(true);
      } else {
        throw new Error(response.data?.message || 'Booking failed');
      }
    } catch (error) {
      console.error('Error processing booking:', error);
      setIsSubmitting(false);
      setIsProcessingPayment(false);
      alert(error.response?.data?.message || error.message || 'Booking failed. Please try again later.');
    }
  };

  /*
  const handlePaymentSuccess = async (response) => {
    // Original payment success logic commented out
  };
  */

  const getTimeSlotLabel = (timeId) => {
    const timeSlots = [
      { id: '09:00', label: '9:00 AM - 11:00 AM' },
      { id: '11:00', label: '11:00 AM - 1:00 PM' },
      { id: '13:00', label: '1:00 PM - 2:00 PM (Lunch)' },
      { id: '14:00', label: '2:00 PM - 4:00 PM' },
      { id: '16:00', label: '4:00 PM - 6:00 PM' },
      { id: '18:00', label: '6:00 PM - 8:00 PM' },
      { id: '20:00', label: '8:00 PM - 10:00 PM' }
    ];
    return timeSlots?.find(slot => slot?.id === timeId)?.label || '';
  };

  const bookingData = {
    name: formData?.name,
    mobile: formData?.mobile,
    date: selectedDate,
    timeSlot: getTimeSlotLabel(selectedTime)
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <CustomerDetailsForm
            formData={formData}
            errors={errors}
            onChange={handleFormDataChange}
          />
        );
      // case 2: // Select Services - hidden, re-enable to restore
      //   return (
      //     <ServiceSelectionForm
      //       selectedServices={selectedServices}
      //       onServiceChange={setSelectedServices}
      //     />
      //   );
      case 3:
        return (
          <DateTimeSelector
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            onDateChange={setSelectedDate}
            onTimeChange={setSelectedTime}
            errors={errors}
          />
        );
      // case 4: // Location & Preferences - hidden, re-enable to restore
      //   return (
      //     <div className="space-y-8">
      //       <LocationSelector
      //         selectedLocation={selectedLocation}
      //         onLocationChange={setSelectedLocation}
      //         error={errors?.location}
      //       />
      //       <SpecialInstructions
      //         instructions={instructions}
      //         preferences={preferences}
      //         onInstructionsChange={setInstructions}
      //         onPreferencesChange={setPreferences}
      //       />
      //     </div>
      //   );
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Schedule Pickup - Maayo Fabric Care | Professional Laundry Service in Coimbatore</title>
        <meta name="description" content="Schedule convenient pickup for your laundry with Maayo Fabric Care. Professional washing, ironing, and dry cleaning services across Coimbatore with free pickup and delivery." />
        <meta name="keywords" content="laundry pickup, schedule pickup, Coimbatore laundry, washing service, dry cleaning, ironing service" />
      </Helmet>

      {/* Main Navigation Header */}
      <Header />

      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-primary text-primary-foreground py-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-3xl md:text-4xl font-bold mb-4">Schedule Your Pickup</h1>
              <p className="text-lg opacity-90 max-w-2xl mx-auto">
                Book convenient pickup for your laundry across Coimbatore. Professional service with free pickup and delivery.
              </p>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              {steps?.map((step, index) => (
                <div key={step?.id} className="flex items-center">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-200 ${currentStep >= step?.id
                      ? 'bg-primary border-primary text-primary-foreground'
                      : 'border-border text-muted-foreground'
                      }`}>
                      {currentStep > step?.id ? (
                        <Icon name="Check" size={20} />
                      ) : (
                        <Icon name={step?.icon} size={20} />
                      )}
                    </div>
                    <div className="hidden sm:block">
                      <p className={`font-medium ${currentStep >= step?.id ? 'text-primary' : 'text-muted-foreground'
                        }`}>
                        {step?.title}
                      </p>
                    </div>
                  </div>

                  {index < steps?.length - 1 && (
                    <div className={`hidden sm:block w-16 h-0.5 mx-4 ${currentStep > step?.id ? 'bg-primary' : 'bg-border'
                      }`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Form Content */}
            <div className="lg:col-span-2">
              <div className="bg-card border border-border rounded-lg p-6">
                {renderStepContent()}

                {/* Navigation Buttons */}
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
                  <Button
                    variant="outline"
                    onClick={handlePrevious}
                    disabled={currentStep === 1}
                    iconName="ChevronLeft"
                    iconPosition="left"
                  >
                    Previous
                  </Button>

                  {currentStep < activeStepIds[activeStepIds.length - 1] ? (
                    <Button
                      variant="default"
                      onClick={handleNext}
                      iconName="ChevronRight"
                      iconPosition="right"
                      className="bg-primary hover:bg-primary/90"
                    >
                      Next Step
                    </Button>
                  ) : (
                    <Button
                      variant="default"
                      onClick={handleSubmit}
                      loading={isSubmitting || isProcessingPayment}
                      iconName="Calendar"
                      iconPosition="left"
                      className="bg-primary hover:bg-primary/90"
                    >
                      {isSubmitting ? 'Scheduling...' : 'Schedule Pickup'}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Booking Summary */}
            <div className="lg:col-span-1">
              <BookingSummary
                formData={formData}
                selectedServices={selectedServices}
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                selectedLocation={selectedLocation}
                preferences={preferences}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting || isProcessingPayment}
              />
            </div>
          </div>
        </div>

        {/* Service Errors Display */}
        {errors?.services && (
          <div className="fixed bottom-4 right-4 bg-destructive text-destructive-foreground p-4 rounded-lg shadow-lg max-w-sm">
            <div className="flex items-start space-x-2">
              <Icon name="AlertCircle" size={20} className="mt-0.5" />
              <div>
                <p className="font-medium">Service Selection Required</p>
                <p className="text-sm opacity-90">{errors?.services}</p>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        <BookingConfirmationModal
          isOpen={showConfirmation}
          onClose={() => setShowConfirmation(false)}
          bookingData={bookingData}
          bookingId={confirmedBookingId}
        />

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default PickupScheduling;
